"use client";

import { useRef } from "react";
import Image from "next/image";
import { ReactNode } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import AnimateOnScroll from "./AnimateOnScroll";

gsap.registerPlugin(ScrollTrigger);

export interface TwoImageItem {
  title: string;
  description: string;
  image: string;
}

interface TwoImageCardProps {
  heading: ReactNode;
  items: [TwoImageItem, TwoImageItem];
  theme?: "blue" | "yellow" | "red";
}

const ImageCard = ({
  item,
  delay = 0,
}: {
  item: TwoImageItem;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      gsap.set(ref.current, { y: 40, opacity: 0 });
      ScrollTrigger.create({
        trigger: ref.current,
        start: "top 92%",
        once: true,
        onEnter: () => {
          gsap.to(ref.current, {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: "power2.out",
            delay,
          });
        },
      });
    },
    { scope: ref },
  );

  return (
    <div ref={ref} className="group relative h-full w-full overflow-hidden">
      <Image
        src={item.image}
        alt={item.title}
        fill
        className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
        sizes="(max-width: 768px) 100vw, 50vw"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.80) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)",
        }}
      />
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-[8px] p-5">
        <h3
          className="font-crimsonText font-bold text-white"
          style={{ fontSize: "clamp(18px, 1.46vw, 28px)", lineHeight: 1.1 }}
        >
          {item.title}
        </h3>
        <p className="font-onest text-[13px] font-light leading-[130%] text-white/90">
          {item.description}
        </p>
      </div>
    </div>
  );
};

const TwoImageCard = ({
  heading,
  items,
  theme = "blue",
}: TwoImageCardProps) => (
  <section className="container bg-white" aria-labelledby="tic-heading">
    <div className="flex flex-col gap-6 md:gap-8 lg:gap-[40px]">
      <AnimateOnScroll>
        <h2
          id="tic-heading"
          className={`heading text-center ${theme === "blue" ? "text-v3-secondary" : "text-black"}`}
        >
          {heading}
        </h2>
      </AnimateOnScroll>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {items.map((item, index) => (
          <div
            key={index}
            className="relative h-[250px] overflow-hidden rounded-lg sm:h-[320px] lg:h-[400px]"
          >
            <ImageCard item={item} delay={index * 0.13} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default TwoImageCard;
